/**
 * Auth Callback Page
 *
 * Handles OAuth redirects, magic link confirmations, and password reset.
 * Magic links and email confirmations use hash fragments (#access_token=...)
 * OAuth uses query parameters (?code=...)
 * Password reset uses ?type=recovery in query params
 */

import { useNavigate } from '@solidjs/router';
import { type Component, createSignal, onMount } from 'solid-js';
import { supabase } from '../lib/supabase';

const AuthCallback: Component = () => {
  const navigate = useNavigate();
  const [error, setError] = createSignal<string | null>(null);

  onMount(async () => {
    try {
      if (!supabase) {
        setError('Supabase not configured');
        return;
      }

      // Check query params first
      const urlParams = new URLSearchParams(window.location.search);
      const isRecovery = urlParams.get('type') === 'recovery';

      // Check if we have hash parameters (magic link / email confirmation / password reset)
      // These come as #access_token=...&type=magiclink or #access_token=...&type=recovery
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const hashType = hashParams.get('type');
      const errorDescription = hashParams.get('error_description');

      // Check for errors in the URL (e.g., expired link)
      if (errorDescription) {
        setError(decodeURIComponent(errorDescription));
        return;
      }

      // Determine if this is a password recovery flow
      const isPasswordRecovery = isRecovery || hashType === 'recovery';

      // If we have tokens in the hash, Supabase will automatically pick them up
      // We need to give it a moment to process and set the session
      if (accessToken) {
        // The Supabase client automatically detects hash params and sets session
        // Wait for the auth state to be updated
        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
          if ((event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') && session) {
            subscription.unsubscribe();
            // Clear the hash from URL for cleaner look
            window.history.replaceState(null, '', window.location.pathname);
            // Redirect to reset password page if this is a recovery flow
            if (isPasswordRecovery) {
              navigate('/reset-password', { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          }
        });

        // Also try getting the session directly after a brief delay
        // This handles the case where the event already fired
        setTimeout(async () => {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (session) {
            subscription.unsubscribe();
            window.history.replaceState(null, '', window.location.pathname);
            if (isPasswordRecovery) {
              navigate('/reset-password', { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          }
        }, 500);

        return;
      }

      // Check for OAuth code in query params
      const code = urlParams.get('code');

      if (code) {
        // Exchange the code for a session
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          setError(exchangeError.message);
          return;
        }
      }

      // Get the current session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        setError(sessionError.message);
        return;
      }

      if (session) {
        // Redirect to reset password page if this is a recovery flow
        if (isPasswordRecovery) {
          navigate('/reset-password', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else {
        // No session and no tokens - something went wrong
        setError('Authentication failed. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    }
  });

  return (
    <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950">
      <div class="text-center">
        {error() ? (
          <div class="space-y-4">
            <div class="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full">
              <svg
                class="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <p class="text-red-400 font-medium">{error()}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              class="text-purple-400 hover:text-purple-300"
            >
              Back to login
            </button>
          </div>
        ) : (
          <div class="space-y-4">
            <div class="inline-flex items-center justify-center w-16 h-16">
              <svg class="animate-spin w-8 h-8 text-purple-500" fill="none" viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <p class="text-gray-300">Completing sign in...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
